import React, { useEffect } from 'react';
import { Dialog, Transition } from '@headlessui/react';
import { Fragment } from 'react';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { XMarkIcon } from '@heroicons/react/24/outline';
import { Team, User, CreateTeamDto } from '../../types';
import LoadingSpinner from '../common/LoadingSpinner';

const schema = yup.object({
  name: yup.string().min(2, 'Team name must be at least 2 characters').required('Team name is required'),
  description: yup.string(),
  color: yup.string().required('Color is required'),
  managerId: yup.string().required('Manager is required'),
});

interface TeamModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (data: any) => void;
  team?: Team | null;
  users: User[];
  isLoading: boolean;
}

const TeamModal: React.FC<TeamModalProps> = ({ isOpen, onClose, onSubmit, team, users, isLoading }) => {
  const isEditing = !!team;

  const getErrorMessage = (error: any) => {
    return error?.message ? String(error.message) : '';
  };

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<any>({
    resolver: yupResolver(schema),
  });

  useEffect(() => {
    if (isOpen) {
      if (team) {
        reset({
          name: team.name,
          description: team.description || '',
          color: team.color,
          managerId: team.manager.id,
        });
      } else {
        reset({
          name: '',
          description: '',
          color: '#3B82F6',
          managerId: '',
        });
      }
    }
  }, [isOpen, team, reset]);

  const handleFormSubmit = (data: any) => {
    onSubmit(data);
  };

  const colorOptions = [
    { value: '#3B82F6', name: 'Blue' },
    { value: '#10B981', name: 'Green' },
    { value: '#F59E0B', name: 'Yellow' },
    { value: '#EF4444', name: 'Red' },
    { value: '#8B5CF6', name: 'Purple' },
    { value: '#F97316', name: 'Orange' },
    { value: '#06B6D4', name: 'Cyan' },
    { value: '#84CC16', name: 'Lime' },
  ];

  const managers = users.filter(user => user.role === 'MANAGER' || user.role === 'ADMIN');

  return (
    <Transition appear show={isOpen} as={Fragment}>
      <Dialog as="div" className="relative z-50" onClose={onClose}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black bg-opacity-25" />
        </Transition.Child>

        <div className="fixed inset-0 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4 text-center">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <Dialog.Panel className="w-full max-w-md transform overflow-hidden rounded-2xl bg-white p-6 text-left align-middle shadow-xl transition-all">
                <div className="flex items-center justify-between mb-4">
                  <Dialog.Title as="h3" className="text-lg font-medium leading-6 text-gray-900">
                    {isEditing ? 'Edit Team' : 'Create New Team'}
                  </Dialog.Title>
                  <button
                    onClick={onClose}
                    className="text-gray-400 hover:text-gray-600"
                  >
                    <XMarkIcon className="h-6 w-6" />
                  </button>
                </div>

                <form onSubmit={handleSubmit(handleFormSubmit)} className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Team Name
                    </label>
                    <input
                      {...register('name')}
                      type="text"
                      className="input-field"
                      placeholder="Development Team"
                    />
                    {errors.name && (
                      <p className="mt-1 text-sm text-red-600">{getErrorMessage(errors.name)}</p>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Description
                    </label>
                    <textarea
                      {...register('description')}
                      rows={3}
                      className="input-field"
                      placeholder="Brief description of the team..."
                    />
                    {errors.description && (
                      <p className="mt-1 text-sm text-red-600">{getErrorMessage(errors.description)}</p>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Team Color
                    </label>
                    <div className="grid grid-cols-4 gap-2">
                      {colorOptions.map((color) => (
                        <label key={color.value} className="flex items-center space-x-2 cursor-pointer">
                          <input
                            {...register('color')}
                            type="radio"
                            value={color.value}
                            className="sr-only"
                          />
                          <div
                            className="w-8 h-8 rounded-full border-2 border-gray-300 flex items-center justify-center"
                            style={{ backgroundColor: color.value }}
                          >
                            <div className="w-4 h-4 rounded-full bg-white opacity-0 transition-opacity" />
                          </div>
                          <span className="text-sm text-gray-700">{color.name}</span>
                        </label>
                      ))}
                    </div>
                    {errors.color && (
                      <p className="mt-1 text-sm text-red-600">{getErrorMessage(errors.color)}</p>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Team Manager
                    </label>
                    <select {...register('managerId')} className="input-field">
                      <option value="">Select a manager</option>
                      {managers.map((manager) => (
                        <option key={manager.id} value={manager.id}>
                          {manager.firstName} {manager.lastName} ({manager.role})
                        </option>
                      ))}
                    </select>
                    {errors.managerId && (
                      <p className="mt-1 text-sm text-red-600">{getErrorMessage(errors.managerId)}</p>
                    )}
                  </div>

                  <div className="flex justify-end space-x-3 pt-4">
                    <button
                      type="button"
                      onClick={onClose}
                      className="btn-secondary"
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      disabled={isLoading}
                      className="btn-primary flex items-center space-x-2"
                    >
                      {isLoading && <LoadingSpinner size="sm" />}
                      <span>{isEditing ? 'Update' : 'Create'}</span>
                    </button>
                  </div>
                </form>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
};

export default TeamModal;
